import React from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import type {
  ParamsProps,
  HeaderRecordProps,
  MessageDataProps,
} from "./dashBoardMesssageStyle";
import {dashBoardMessageStyle} from "./dashBoardMesssageStyle";
import {store, useAppSelector} from "../../../../redux/store";
import {
  base64ToBlob,
  isValidObject,
  loggedMessage,
  truncateText,
} from "commonUtils/utils";
import {
  Alerts,
  AxiosErrorCode,
  CPSConstants,
  CPSMessages,
  HttpStatusCode,
  PageNames,
} from "commonUtils/enumerations";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import {
  HttpException,
  ServiceAuthenticationException,
} from "services/exceptionHandlerService";
import {logMessage} from "services/loggerService";
import ViewAttachmentDialog from "components/Dialogs/ViewAttachmentDialog";
import {Loader} from "components/FormComponents/Loader/Loader";
import "../../Dashboard.css";
import {postRequest, setHeader} from "repository/genericRepository";
import {
  ANNOUNCEMENT_VIEW_ALL_ENDPOINT,
  DASHBOARD_MESSAGE_ATTACH_ENDPOINT,
} from "commonUtils/globalAPISettings";
import MessageCenterViewDialog from "components/Dialogs/MessageCenterViewDialog";
import MessageListDialog from "components/Dialogs/MessageListDialog";
import DOMPurify from "dompurify";
import "../../../Dashboard/Dashboard.css";

interface Props {
  getDashboard: () => void;
  deleteMasterMessage: any;
  messagesData: any;
  isViewAllAnnouncement: boolean | undefined;
  announcementdialogopen: boolean;
  setAnnounceDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deletedMessage: string;
  showAlert: boolean;
}

const DashboardMessages: React.FC<Props> = ({
  getDashboard,
  deleteMasterMessage,
  messagesData,
  isViewAllAnnouncement,
  announcementdialogopen,
  setAnnounceDialogOpen,
  deletedMessage,
  showAlert,
}) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const [messages, setMessages] = React.useState<MessageDataProps[]>([]);
  const [announceDialogData, setAnnounceDialogData] = React.useState<
    MessageDataProps[]
  >([]);
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const [attachdialog, setOpenAttachDialog] = React.useState<boolean>(false);
  const [basestring, setBaseString] = React.useState<string>("");
  const [loader, setLoader] = React.useState<boolean>(false);
  const [viewAllRecords, setViewAllRecords] = React.useState<any>([]);
  const [listMessageDialog, setListMessageDialogOpen] =
    React.useState<boolean>(false);
  const [messageYear, setMessageYear] = React.useState(
    new Date().getFullYear(),
  );

  const {userDetails} = useAppSelector(state => {
    return state.auth;
  });

  React.useEffect(() => {
    setMessages(messagesData);
  }, [messagesData]);

  const columns: HeaderRecordProps[] = [
    {
      field: "fromDate",
      hideable: false,
      headerName: "StartDate",
      minWidth: 120,
      headerAlign: "left",
      editable: false,
    },
    {
      field: "content",
      hideable: false,
      headerName: "Message",
      minWidth: 650,
      headerAlign: "left",
      wrap: true,
      editable: false,
      renderCell: (params: any) => {
        const {content} = params.row as ParamsProps;
        return (
          <Box
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(content),
            }}
          />
        );
      },
    },
    {
      field: "sentBy",
      hideable: false,
      headerName: "Sentby",
      minWidth: 200,
      headerAlign: "left",
      editable: false,
    },
    {
      field: "delete",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerName: "Action",
      minWidth: 60,
      headerAlign: "center",
      editable: false,
      flex: 1,
      renderCell: (params: any) => (
        <Box className="delete-messages-viewall">
          <DeleteIcon
            onClick={() => {
              handleDelete(params.row.announcementKey, "viewall");
            }}
          />
        </Box>
      ),
    },
  ];

  const handleDelete = (id: number, type: string) => {
    try {
      logMessage(
        "INFO",
        `Master ${userDetails?.userName} has deleted the Message.`,
        PageNames.DASHBOARD,
      );
      deleteMasterMessage(id);
      scrollToTop();
      if (type === "viewall") {
        const filteredArr = viewAllRecords?.filter(
          ({announcementKey}: {announcementKey: number}) =>
            announcementKey !== id,
        );
        setViewAllRecords(filteredArr);
        setMessages(filteredArr);
        getDashboard();
      } else {
        const filteredArr = messagesData?.filter(
          (el: any) => el?.announcementKey !== id,
        );
        setMessages(filteredArr);
        getDashboard();
      }
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  };

  const scrollToTop = () => {
    if (dialogRef.current) {
      dialogRef.current.scrollTop = 0;
    }
  };

  const viewAllAnnouncement = async (year?: string) => {
    setListMessageDialogOpen(true);
    try {
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      const announcementKey = 0;
      const profileID = authState.auth?.userDetails?.profileId;
      const announcementYear = new Date().getFullYear();
      setLoader(true);
      const responseData = await postRequest({
        url: ANNOUNCEMENT_VIEW_ALL_ENDPOINT,
        headers: setHeader(APIHeaders),
        params: {announcementKey, profileID, announcementYear},
      });
      if (isValidObject(responseData)) {
        setViewAllRecords(responseData.announcements);
        setMessageYear(new Date().getFullYear());
      }
      setLoader(false);
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.DASHBOARD);
      setLoader(false);
      handleAxiosErrors(error);
    }
  };

  const callAttachmentRecord = async (id: number) => {
    try {
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      const announcementKey = id;
      const profileID = authState.auth?.userDetails?.profileId;
      setLoader(true);
      const responseData = await postRequest({
        url: DASHBOARD_MESSAGE_ATTACH_ENDPOINT,
        headers: setHeader(APIHeaders),
        params: {announcementKey, profileID},
      });
      if (isValidObject(responseData)) {
        const blob = base64ToBlob(
          responseData?.file,
          responseData?.fileExtension,
        );
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const fileName = responseData?.fileName;
        link.href = url;
        link.setAttribute("download", fileName);
        link.click();
        responseData?.fileExtension.includes(".pdf") && setBaseString(url);
      }
      setLoader(false);
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.DASHBOARD);
      setLoader(false);
      handleAxiosErrors(error);
    }
  };

  const handleAnnouncement = (id: number) => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} has opend the Show Announcement dialog.`,
        PageNames.DASHBOARD,
      );
      setAnnounceDialogOpen(true);
      const filteredArr = messages?.filter(
        (el: any) => el?.announcementKey === id,
      );
      setAnnounceDialogData(filteredArr);
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  };

  const handleAxiosErrors = (error: any) => {
    const data = {pagename: PageNames.DASHBOARD};
    if (error?.code === AxiosErrorCode.ERR_BAD_REQUEST) {
      if (
        error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.response?.status === HttpStatusCode.FORBIDDEN
      ) {
        ServiceAuthenticationException(error, data, error?.response?.status);
      } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      } else if (
        error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
      ) {
        HttpException(error, data);
        setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      } else {
        setErrorMsg(error.message);
      }
    } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
      setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
      setErrorMsg(CPSMessages.NETWORK_ERROR);
    } else {
      setErrorMsg(error.message);
    }
  };

  const openAttachment = (id: number) => {
    callAttachmentRecord(id);
    setOpenAttachDialog(true);
  };

  const handleClose = () => {
    setOpenAttachDialog(false);
    setBaseString("");
  };

  return (
    <>
      <Loader open={loader} />

      <ViewAttachmentDialog
        dialogPaperProps={dashBoardMessageStyle.paperPropsViewAttachDialog}
        title="View Attachment"
        loader={loader}
        filelink={basestring}
        setLoader={setLoader}
        open={attachdialog}
        dialogStyle={{
          centerTheDialogContent: dashBoardMessageStyle.centerTheDialogContent,
          iframeContent: dashBoardMessageStyle.iframeContent,
        }}
        handleClose={handleClose}
      />

      <MessageCenterViewDialog
        announcementdialogopen={announcementdialogopen}
        announceDialogData={announceDialogData}
        setAnnounceDialogOpen={setAnnounceDialogOpen}
        openAttachment={openAttachment}
        handleDelete={handleDelete}
      />

      <MessageListDialog
        listMessageDialog={listMessageDialog}
        viewAllRecords={viewAllRecords}
        setViewAllRecords={setViewAllRecords}
        showAlert={showAlert}
        deletedMessage={deletedMessage}
        setListMessageDialogOpen={setListMessageDialogOpen}
        columns={columns}
        loader={loader}
        setLoader={setLoader}
        errorMsg={errorMsg}
        setErrorMsg={setErrorMsg}
        messages={messages}
        messageYear={messageYear}
        setMessageYear={setMessageYear}
        dialogRef={dialogRef}
      />

      {messages !== undefined &&
      Object.keys(messages).length !== 0 &&
      messages !== null ? (
        <Paper className="paper-look">
          {errorMsg && (
            <FormValidationAlert severity={Alerts.ERROR} message={errorMsg} />
          )}
          <Grid container spacing={0}>
            <Grid item xs={6} className="display-flex justify-content-start">
              <Box component="div" className="font-weight-bold messages">
                <EmailOutlinedIcon className="outline-icon" />
                {" Messages" + " ( " + messages?.length + " )"}
              </Box>
            </Grid>
            {isViewAllAnnouncement && (
              <Grid item xs={6} className="display-flex justify-content-end">
                <Box
                  component="div"
                  onClick={() => {
                    viewAllAnnouncement();
                  }}
                  className="messages view-all">
                  <ViewListIcon className="outline-icon" />
                  View All
                </Box>
              </Grid>
            )}
          </Grid>
          <Box className="divider-section" />
          <Box className="box-padding-bottom" />
          <List className="my-list message-alert-block">
            {Object.keys(messages)?.length !== 0 ? (
              messages?.map((a: any, index: number) => (
                <>
                  <ListItem
                    key={index}
                    alignItems="flex-start"
                    className="display-flex align-items-baseline">
                    <ListItemText
                      primary={
                        <>
                          <Typography
                            component="span"
                            className="alert-name cursor-pointer"
                            onClick={() => {
                              handleAnnouncement(a?.announcementKey);
                            }}>
                            {a?.subject}
                          </Typography>
                        </>
                      }
                      secondary={
                        <>
                          <Typography
                            fontWeight={"bold"}
                            component="span"
                            fontSize={CPSConstants.FONTREMTEXT}
                            color="#666666">
                            {a?.content ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    truncateText(a?.content, 150),
                                  ),
                                }}
                              />
                            ) : (
                              <div></div>
                            )}
                          </Typography>
                        </>
                      }
                    />
                    <Button
                      variant="text"
                      className="delete-messages"
                      onClick={() => {
                        handleDelete(a?.announcementKey, "");
                      }}>
                      Delete
                    </Button>
                  </ListItem>
                  <Divider />
                </>
              ))
            ) : (
              <Box height={"10rem"} sx={{marginTop: "2rem"}}>
                <Grid container spacing={2} m={"-2rem"}>
                  <Grid item xs={11}>
                    <Skeleton variant="text" />
                  </Grid>
                  <Grid item xs={1}>
                    <Skeleton variant="text" />
                  </Grid>
                  <Grid item xs={11}>
                    <Skeleton variant="text" />
                  </Grid>
                  <Grid item xs={1}>
                    <Skeleton variant="text" />
                  </Grid>
                </Grid>
              </Box>
            )}
          </List>
        </Paper>
      ) : (
        <Paper className="paper-look">
          <Grid container spacing={0}>
            <Grid item xs={6} display={"flex"} justifyContent={"start"}>
              <Typography
                component="div"
                fontWeight={"bold"}
                className="view-all">
                <EmailOutlinedIcon className="outline-icon" />
                {" Messages" + " ( 0 )"}
              </Typography>
            </Grid>
            {isViewAllAnnouncement && (
              <Grid item xs={6} display={"flex"} justifyContent={"end"}>
                <Box
                  component="div"
                  onClick={() => {
                    viewAllAnnouncement();
                  }}
                  className="messages view-all">
                  <ViewListIcon className="email-outline-icon" />
                  View All
                </Box>
              </Grid>
            )}
          </Grid>

          <Box className="my-border" />
          <Box className="box-padding-bottom" />
          <List className="message-my-list">
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <>
                    <Typography>{""}</Typography>
                  </>
                }
              />
              <Typography>{""}</Typography>
            </ListItem>
          </List>
          <List className="recordsnotfound">
            {CPSMessages.DASHBOARDMSGNODATA}
          </List>
        </Paper>
      )}
    </>
  );
};

export default DashboardMessages;
